/**
 * usePortfolios Hook
 * 
 * Fetches and manages portfolios list data with opportunity stats.
 * Isolates API calls from UI components per Rule 6.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { authApi } from '../shared/utils/api';
import { Portfolio } from '../shared/types/portfolio';
import { logger } from '../shared/utils/logger';

// Extended portfolio with computed stats
export interface PortfolioWithStats extends Portfolio {
  opportunityCount: number;
  totalValue: number | null;
}

interface UsePortfoliosResult {
  portfolios: PortfolioWithStats[];
  filteredPortfolios: PortfolioWithStats[];
  search: string;
  setSearch: (search: string) => void;
  loading: boolean;
  refreshing: boolean;
  refresh: () => Promise<void>;
  error: boolean;
}

export function usePortfolios(): UsePortfoliosResult {
  const [portfolios, setPortfolios] = useState<PortfolioWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState(false);

  const fetchPortfolios = useCallback(async (isRefresh = false) => {
    try {
      setError(false);
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      // Fetch portfolios list
      const response = await authApi.getPortfolios();
      const basePortfolios = response.portfolios || [];
      
      // Fetch opportunity data for each portfolio to get counts and values
      const portfoliosWithStats: PortfolioWithStats[] = await Promise.all(
        basePortfolios.map(async (portfolio) => {
          try {
            const oppResponse = await authApi.getOpportunities(portfolio.id);
            const opportunities = oppResponse.opportunities || [];
            
            logger.debug('Portfolio opportunities fetched', 'portfolio', {
              portfolioId: portfolio.id,
              portfolioName: portfolio.name,
              count: opportunities.length,
            });
            
            // Calculate stats
            const opportunityCount = opportunities.length;
            // Use asking_price (listing price) as primary, fall back to property_value (tax assessed)
            const totalValue = opportunities.reduce((sum, opp) => {
              const value = opp.asking_price || opp.property_value || 0;
              return sum + (typeof value === 'number' ? value : 0);
            }, 0);
            
            return {
              ...portfolio,
              opportunityCount,
              totalValue: totalValue > 0 ? totalValue : null,
            };
          } catch (err) {
            // If fetching opportunities fails, return portfolio with zero counts
            logger.warn('Failed to fetch opportunities for portfolio', 'portfolio', {
              portfolioId: portfolio.id,
              error: err instanceof Error ? err.message : String(err),
            });
            return {
              ...portfolio,
              opportunityCount: 0,
              totalValue: null,
            };
          }
        })
      );
      
      setPortfolios(portfoliosWithStats);
    } catch (err) {
      logger.error('Failed to fetch portfolios', 'portfolio');
      setError(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchPortfolios();
  }, [fetchPortfolios]);

  const refresh = useCallback(() => fetchPortfolios(true), [fetchPortfolios]);

  // Filter portfolios by search
  const filteredPortfolios = useMemo(() => {
    if (!search.trim()) return portfolios;
    const searchLower = search.toLowerCase();
    return portfolios.filter(p => p.name.toLowerCase().includes(searchLower));
  }, [portfolios, search]);

  return {
    portfolios,
    filteredPortfolios,
    search,
    setSearch,
    loading,
    refreshing,
    refresh,
    error,
  };
}
